import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { createHealthIncident, updateHealthIncident } from '../../../../store/participantService';
import { todayDate } from '../../../../utils/dateFunctions';
import { getObjFromId } from '../../../../utils/directusFunctions';
import { regExps } from '../../../../utils/formValidation/validationPatterns';
import DateSelect from '../../../formElements/DateSelect';
import MultiSelect from '../../../formElements/MultiSelect';
import RadioButtons from '../../../formElements/RadioButtons';
import SingleSelect from '../../../formElements/SingleSelect';
import TextAreaField from '../../../formElements/TextAreaField';
import TextInputField from '../../../formElements/TextInputField';
import FormActions from '../../../ui/formActions/FormActions';

import { validate } from './validateHealthIncident';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';

const _NOTE_CHAR_LIMIT = 500;
const _OTHER_CHAR_LIMIT = 50;

const EditHealthIncident = ({
    participant,
    onClose,
    isDisabled,
    attendedModules,
    linkedModuleId,
    formType,
    row = {}
}) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const _MESSAGE = `Participant health incident has been ${
        formType === 'create' ? 'added' : 'updated'
    }`;

    const initialState = {
        id: '',
        participantId: '',
        eventDate: todayDate(),
        healthEventTypeIds: [],
        sideEffectsDetail: '',
        adverseEventsDetail: '',
        capillaryBloodGlucoseFlagDate: '',
        bloodPressureFlagDate: '',
        alertsDiscussedWithMedicalDirector: false,
        uniqueAlertsToPrimaryCare: '',
        uniqueAlertsDate: '',
        programmeDeIntensifiedDate: '',
        programmeReIntensifiedDate: '',
        fibreSupplementDate: '',
        dateReportedToNhse: '',
        note: '',
        linkedModuleId: '',
        inactive: false
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialRow, setInitialRow] = useState({});
    const [modules, setModules] = useState([]);
    const [errors, setErrors] = useState({});
    const [otherSideEffectEventTypeId, setOtherSideEffectEventTypeId] = useState('');
    const [otherAdverseEventEventTypeId, setOtherAdverseEventEventTypeId] = useState('');
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const healthEventTypes = useSelector(
        (state) => state.entities.directusService.healthEventTypes
    );

    // USEEFFECTS
    useEffect(() => {
        if (!healthEventTypes.length) return;

        setOtherSideEffectEventTypeId(
            healthEventTypes.find((el) => el.name.toLowerCase() === 'other - side effect')?.id || ''
        );
        setOtherAdverseEventEventTypeId(
            healthEventTypes.find((el) => el.name.toLowerCase() === 'other - adverse event')?.id ||
                ''
        );
        if (formType === 'create' && linkedModuleId) {
            setNewEntry({ ...initialState, linkedModuleId });
        }
        if (formType === 'edit') {
            const updatedRow = {
                ...row,
                sideEffectsDetail: row.sideEffectsDetail ?? '',
                adverseEventsDetail: row.adverseEventsDetail ?? '',
                note: row.note ?? ''
            };
            setNewEntry(updatedRow);
            setInitialRow(updatedRow);
        }
    }, [formType, row, healthEventTypes]);

    useEffect(() => {
        let modules = [...attendedModules];
        if (formType === 'edit') {
            if (
                row.linkedModuleId &&
                row.linkedModule &&
                !modules.find((el) => el.id === row.linkedModuleId)
            ) {
                modules = [...modules, { id: row.linkedModuleId, name: row.linkedModule }];
            }
        }
        setModules(modules);
    }, [attendedModules]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // FNs
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateUniqueAlerts = (e) => {
        const val = e.target.value.replace(regExps.DIGITS_R, '');
        setNewEntry((prev) => ({ ...prev, uniqueAlertsToPrimaryCare: val }));
    };

    const onUpdateEventTypes = (chosenIds) => {
        clearError('sideEffectsDetail');
        clearError('adverseEventsDetail');
        if (!chosenIds.includes(otherSideEffectEventTypeId) && newEntry.sideEffectsDetail)
            onUpdate('sideEffectsDetail', '');
        if (!chosenIds.includes(otherAdverseEventEventTypeId) && newEntry.adverseEventsDetail)
            onUpdate('adverseEventsDetail', '');
        onUpdate('healthEventTypeIds', chosenIds);
    };

    const onUpdateEventDate = (date) => {
        clearError('dateReportedToNhse');
        onUpdate('eventDate', date);
    };

    const onUpdateProgrammeDeIntensifiedDate = (date) => {
        clearError('programmeReIntensifiedDate');
        clearError('fibreSupplementDate');
        onUpdate('programmeDeIntensifiedDate', date);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const payload = { ...newEntry, participantId: participant?.id };
        const newErrors = validate(
            payload,
            initialRow,
            formType,
            otherSideEffectEventTypeId,
            otherAdverseEventEventTypeId
        );
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        formType === 'create'
            ? dispatch(createHealthIncident(payload, _MESSAGE))
            : dispatch(updateHealthIncident(payload.id, payload, _MESSAGE));
        onClose();
    };

    // RENDER
    return (
        <div className={`${form.formWrapper} ${app.fadein}`}>
            <form className={form.form} onSubmit={onSubmit} data-testid="edit_health_incident_form">
                <h2 className={app.sectionHeading}>
                    {formType === 'create' ? 'Add a New Health Incident' : 'Edit Health Incident'}
                </h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <DateSelect
                            id="eventDate"
                            label="Event Date"
                            value={newEntry.eventDate}
                            disabled={isDisabled}
                            mandatory={true}
                            error={errors.eventDate}
                            onDateChange={(date) => onUpdateEventDate(date)}
                        />

                        <DateSelect
                            id="capillaryBloodGlucoseFlagDate"
                            label="Capillary Blood Glucose Flag Date"
                            value={newEntry.capillaryBloodGlucoseFlagDate}
                            disabled={isDisabled}
                            onDateChange={(date) => onUpdate('capillaryBloodGlucoseFlagDate', date)}
                        />

                        <DateSelect
                            id="bloodPressureFlagDate"
                            label="Blood Pressure Flag Date"
                            value={newEntry.bloodPressureFlagDate}
                            disabled={isDisabled}
                            onDateChange={(date) => onUpdate('bloodPressureFlagDate', date)}
                        />

                        <RadioButtons
                            id="alertsDiscussedWithMedicalDirector"
                            label="Alerts Discussed with Medical Director"
                            value={newEntry.alertsDiscussedWithMedicalDirector || false}
                            onChange={(option) =>
                                onUpdate('alertsDiscussedWithMedicalDirector', option)
                            }
                        />

                        <TextInputField
                            id="uniqueAlertsToPrimaryCare"
                            label="No. Unique Alerts to Primary Care"
                            placeholder="Enter number of unique alerts..."
                            disabled={isDisabled}
                            maxLength={2}
                            value={newEntry.uniqueAlertsToPrimaryCare || ''}
                            customClass="shorterInput"
                            onChange={onUpdateUniqueAlerts}
                        />

                        <DateSelect
                            id="uniqueAlertsDate"
                            label="Unique Alerts Date"
                            value={newEntry.uniqueAlertsDate}
                            disabled={isDisabled}
                            onDateChange={(date) => onUpdate('uniqueAlertsDate', date)}
                        />

                        <TextAreaField
                            id="note"
                            label="Notes"
                            placeholder="Enter notes..."
                            disabled={isDisabled}
                            maxLength={_NOTE_CHAR_LIMIT}
                            value={newEntry.note}
                            rows={8}
                            count={`${newEntry.note?.length || 0}/${_NOTE_CHAR_LIMIT}`}
                            onChange={(e) => onUpdate('note', e.target.value)}
                        />
                    </div>

                    <div className={form.formColumn}>
                        <MultiSelect
                            id="healthEventTypeIds"
                            label="Event Types"
                            placeholder="Select event types..."
                            disabled={isDisabled}
                            mandatory={true}
                            menuItems={healthEventTypes || []}
                            preSelectedIds={newEntry.healthEventTypeIds || []}
                            preSelects={healthEventTypes.filter((el) =>
                                newEntry.healthEventTypeIds?.includes(el.id)
                            )}
                            error={errors.healthEventTypeIds}
                            onChange={(chosenIds) => onUpdateEventTypes(chosenIds)}
                        />

                        {newEntry.healthEventTypeIds.includes(otherSideEffectEventTypeId) && (
                            <TextAreaField
                                id="sideEffectsDetail"
                                label="Other - Side Effect Event Type Details"
                                placeholder="Enter details for Other - Side Effect event type..."
                                disabled={isDisabled}
                                mandatory={newEntry.healthEventTypeIds.includes(
                                    otherSideEffectEventTypeId
                                )}
                                maxLength={_OTHER_CHAR_LIMIT}
                                rows={3}
                                value={newEntry.sideEffectsDetail}
                                count={`${newEntry.sideEffectsDetail?.length || 0}/${_OTHER_CHAR_LIMIT}`}
                                error={errors.sideEffectsDetail}
                                onChange={(e) => onUpdate('sideEffectsDetail', e.target.value)}
                            />
                        )}
                        {newEntry.healthEventTypeIds.includes(otherAdverseEventEventTypeId) && (
                            <TextAreaField
                                id="adverseEventsDetail"
                                label="Other - Adverse Event Event Type Details"
                                placeholder="Enter details for Other - Adverse Event event type..."
                                disabled={isDisabled}
                                mandatory={newEntry.healthEventTypeIds.includes(
                                    otherAdverseEventEventTypeId
                                )}
                                maxLength={_OTHER_CHAR_LIMIT}
                                rows={3}
                                value={newEntry.adverseEventsDetail}
                                count={`${newEntry.adverseEventsDetail?.length || 0}/${_OTHER_CHAR_LIMIT}`}
                                error={errors.adverseEventsDetail}
                                onChange={(e) => onUpdate('adverseEventsDetail', e.target.value)}
                            />
                        )}

                        <DateSelect
                            id="programmeDeIntensifiedDate"
                            label="Programme De-intensified Date"
                            value={newEntry.programmeDeIntensifiedDate}
                            disabled={isDisabled}
                            error={errors.programmeDeIntensifiedDate}
                            onDateChange={(date) => onUpdateProgrammeDeIntensifiedDate(date)}
                        />

                        <DateSelect
                            id="programmeReIntensifiedDate"
                            label="Programme Re-intensified Date"
                            value={newEntry.programmeReIntensifiedDate}
                            disabled={isDisabled}
                            error={errors.programmeReIntensifiedDate}
                            onDateChange={(date) => onUpdate('programmeReIntensifiedDate', date)}
                        />

                        <DateSelect
                            id="fibreSupplementDate"
                            label="Fibre Supplement Date"
                            value={newEntry.fibreSupplementDate}
                            disabled={isDisabled}
                            error={errors.fibreSupplementDate}
                            onDateChange={(date) => onUpdate('fibreSupplementDate', date)}
                        />

                        <DateSelect
                            id="dateReportedToNhse"
                            label="Date Reported to NHSE"
                            value={newEntry.dateReportedToNhse}
                            disabled={isDisabled}
                            error={errors.dateReportedToNhse}
                            onDateChange={(date) => onUpdate('dateReportedToNhse', date)}
                        />

                        <SingleSelect
                            id="linkedModuleId"
                            label="Linked Module"
                            placeholder="Select linked module..."
                            disabled={isDisabled}
                            menuItems={modules || []}
                            selectedId={newEntry.linkedModuleId}
                            selected={getObjFromId(modules, newEntry.linkedModuleId)}
                            onChange={(chosenId) => onUpdate('linkedModuleId', chosenId)}
                        />
                    </div>
                </div>

                <FormActions
                    onClose={onSubmit}
                    onCancel={onClose}
                    btnText={formType === 'create' ? 'Save' : 'Update'}
                    disabled={isButtonDisabled || isDisabled}
                    customClass="noTopBorder"
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

EditHealthIncident.propTypes = {
    participant: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    isDisabled: PropTypes.bool,
    formType: PropTypes.string,
    row: PropTypes.object,
    attendedModules: PropTypes.arrayOf(PropTypes.object),
    linkedModuleId: PropTypes.string
};

export default EditHealthIncident;
